import { Link } from 'react-router-dom';
import {
  UXP_KC_ID,
  COMMAND_CENTER_METRICS,
  KNOWLEDGE_BASE_TYPES,
  LEARNING_FEATURES,
  TUTOR_CAPABILITIES,
  SOP_FEATURES,
  GRAPH_ENTITY_TYPES,
  SEARCH_MODES,
  DOCUMENTATION_FEATURES,
  AI_GENERATION_TYPES,
  ORG_MEMORY_TYPES,
  AGENT_TRAINING_FEATURES,
  CERTIFICATION_TYPES,
  KNOWLEDGE_ANALYTICS,
  COLLABORATION_FEATURES,
  ACCESSIBILITY_STANDARDS,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_KPIS,
  DEMO_ARTICLES,
  DEMO_COURSES,
  DEMO_TUTOR,
  DEMO_SOPS,
  DEMO_GRAPH,
  DEMO_SEARCH,
  DEMO_DOCUMENTS,
  buildKnowledgeCenterDashboard,
} from '../../knowledgecenter/framework';
import {
  KnowledgeCommandCenterPanel,
  KnowledgeBasePanel,
  LearningAcademyPanel,
  AITutorPanel,
  SOPManagementPanel,
  KnowledgeGraphPanel,
  SearchEnginePanel,
  DocumentationStudioPanel,
} from '../../components/knowledgecenter';
import { Card, KpiCard } from '../../components/ui';

export function KnowledgeCenterPage() {
  const dashboard = buildKnowledgeCenterDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{UXP_KC_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">AI Knowledge Center</h1>
        <p className="mt-1 text-sm text-slate-500">
          AI Knowledge Center, Learning Academy, Documentation & Organizational Intelligence Operating System —
          the permanent organizational knowledge layer for the AI Commerce Operating System.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Command Center Metrics" value={dashboard.command_center_metrics.toString()} />
        <KpiCard label="Knowledge Base Types" value={dashboard.knowledge_base_types.toString()} />
        <KpiCard label="Learning Features" value={dashboard.learning_features.toString()} />
        <KpiCard label="Search Modes" value={dashboard.search_modes.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Architecture Pipeline</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Knowledge Command Center</h2>
        <p className="mt-1 text-sm text-slate-500">{COMMAND_CENTER_METRICS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <KnowledgeCommandCenterPanel kpis={DEMO_KPIS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Knowledge Base</h2>
        <p className="mt-1 text-sm text-slate-500">{KNOWLEDGE_BASE_TYPES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <KnowledgeBasePanel articles={DEMO_ARTICLES} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Learning Academy</h2>
          <p className="mt-1 text-sm text-slate-500">{LEARNING_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <LearningAcademyPanel courses={DEMO_COURSES} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">AI Tutor</h2>
          <p className="mt-1 text-sm text-slate-500">{TUTOR_CAPABILITIES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <AITutorPanel sessions={DEMO_TUTOR} />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">SOP Management</h2>
        <p className="mt-1 text-sm text-slate-500">{SOP_FEATURES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <SOPManagementPanel sops={DEMO_SOPS} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Knowledge Graph</h2>
          <p className="mt-1 text-sm text-slate-500">{GRAPH_ENTITY_TYPES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <KnowledgeGraphPanel nodes={DEMO_GRAPH} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Search Engine</h2>
          <p className="mt-1 text-sm text-slate-500">{SEARCH_MODES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <SearchEnginePanel results={DEMO_SEARCH} />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Documentation Studio</h2>
        <p className="mt-1 text-sm text-slate-500">{DOCUMENTATION_FEATURES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <DocumentationStudioPanel documents={DEMO_DOCUMENTS} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">AI Knowledge Generation</h2>
          <p className="mt-1 text-sm text-slate-500">{AI_GENERATION_TYPES.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Organizational Memory</h2>
          <p className="mt-1 text-sm text-slate-500">{ORG_MEMORY_TYPES.join(' · ')}</p>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">AI Agent Training</h2>
          <p className="mt-2 text-xs text-slate-500">{AGENT_TRAINING_FEATURES.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Certification Center</h2>
          <p className="mt-2 text-xs text-slate-500">{CERTIFICATION_TYPES.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Knowledge Analytics</h2>
          <p className="mt-2 text-xs text-slate-500">{KNOWLEDGE_ANALYTICS.join(' · ')}</p>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Collaboration</h2>
          <p className="mt-2 text-xs text-slate-500">{COLLABORATION_FEATURES.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Accessibility</h2>
          <div className="mt-2 flex flex-wrap gap-1">
            {ACCESSIBILITY_STANDARDS.map((s) => (
              <span key={s} className="rounded border border-slate-200 px-2 py-0.5 text-xs dark:border-slate-700">{s}</span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
